import { recordSymptom, updateSymptom } from "@repo/data-store";
import { asConditionId, asObservationId, asPatientId, type ObservationId } from "@repo/domain";
import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";
import { type CodeValue, hasCode, toCodeableConcept } from "@/shared/forms/code-value";

// === Schema & Types ===

export const symptomFormSchema = z.object({
  patientId: z.string().min(1, "Select a patient"),
  code: z.custom<CodeValue | undefined>().refine(hasCode, { message: "Select a symptom" }),
  source: z.enum(["patient-reported", "clinician-observed"]),
  severity: z.custom<CodeValue | undefined>().nullable(),
  note: z.string(),
  attachedConditionIds: z.array(z.string()),
});

export type SymptomFormValues = z.infer<typeof symptomFormSchema>;

export const DEFAULT_SYMPTOM_FORM_VALUES: SymptomFormValues = {
  patientId: "",
  code: undefined,
  source: "clinician-observed",
  severity: null,
  note: "",
  attachedConditionIds: [],
};

// === Submit ===

/** Records a symptom Observation and optionally attaches it to N Conditions. */
export function recordSymptomFromForm(value: SymptomFormValues, id: string): void {
  if (!value.code) return;
  recordSymptom({
    id,
    patient: { id: asPatientId(value.patientId) },
    code: toCodeableConcept(value.code),
    source: value.source,
    ...(value.severity ? { severity: toCodeableConcept(value.severity) } : {}),
    ...(value.note.trim() ? { note: value.note.trim() } : {}),
    attachedTo: value.attachedConditionIds.map((conditionId) => ({ conditionId: asConditionId(conditionId) })),
  });
}

/** Corrects a recorded symptom's source/severity/note. Code is immutable; attachments untouched. */
export function updateSymptomFromForm(value: SymptomFormValues, observationId: ObservationId): void {
  updateSymptom({
    observationId: asObservationId(observationId),
    source: value.source,
    ...(value.severity ? { severity: toCodeableConcept(value.severity) } : {}),
    note: value.note.trim(),
  });
}

export { useAppForm };
